import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  Plus, Trash2, Send, CheckCircle, AlertCircle, Phone,
} from "lucide-react";

// ── API ──────────────────────────────────────────────────────────
const WHATSAPP_BULK_URL = "https://europe-west3-metarelm.cloudfunctions.net/whatsappBulk";

// ── Design tokens ────────────────────────────────────────────────
const T = {
  bg:      "#FFFFFF",
  bgSoft:  "#F8F8FC",
  bgMuted: "#F0F0F6",
  dark:    "#0A0A0F",
  text:    "#1A1A2E",
  muted:   "#8080A0",
  dim:     "#B0B0C4",
  border:  "#E4E4EF",
  blue:    "#007BFF",
  red:     "#FF3D00",
  orange:  "#FF6B00",
  gold:    "#FFB800",
  green:   "#25D366", // WhatsApp green
  white:   "#FFFFFF",
};
const GRAD      = `linear-gradient(90deg,${T.blue} 0%,${T.red} 40%,${T.orange} 70%,${T.gold} 100%)`;
const GRAD_SIZE = "300% 100%";

// ── CSS ──────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;background:#fff;color:${T.dark};}
  @keyframes gradFlow{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  .grad-text{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 5s ease infinite;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .grad-bg{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;}
  .wa-input{width:100%;padding:11px 14px;background:${T.white};border:1.5px solid ${T.border};border-radius:10px;color:${T.dark};font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s,box-shadow .2s;}
  .wa-input:focus{border-color:${T.green};box-shadow:0 0 0 3px rgba(37,211,102,.1);}
  .wa-input::placeholder{color:#c0c0d0;}
  .wa-textarea{width:100%;padding:11px 14px;min-height:100px;resize:vertical;background:${T.white};border:1.5px solid ${T.border};border-radius:10px;color:${T.dark};font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s;}
  .wa-textarea:focus{border-color:${T.green};box-shadow:0 0 0 3px rgba(37,211,102,.1);}
  .wa-textarea::placeholder{color:#c0c0d0;}
  .btn-primary{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;border-radius:40px;border:none;background:#25D366;color:#fff;font-family:'Sora',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:transform .15s,box-shadow .2s;white-space:nowrap;}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(37,211,102,.35);}
  .btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;background:${T.muted};}
  .btn-ghost{display:inline-flex;align-items:center;gap:7px;padding:10px 18px;border-radius:40px;background:${T.bgSoft};border:1.5px solid ${T.border};color:${T.muted};font-family:'Sora',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:background .2s,color .2s;}
  .btn-ghost:hover{background:${T.border};color:${T.dark};}
  .btn-icon{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:8px;border:1.5px solid ${T.border};background:${T.white};cursor:pointer;transition:background .2s,border-color .2s;color:${T.muted};flex-shrink:0;}
  .btn-icon:hover{background:#fff0f0;border-color:#ffcccc;color:#e84020;}
  .btn-icon:disabled{opacity:.35;cursor:not-allowed;}
  .lbl{display:block;font-size:10px;font-weight:700;color:${T.green};margin-bottom:6px;text-transform:uppercase;letter-spacing:.1em;font-family:'Sora',sans-serif;}
  .phone-row{display:grid;grid-template-columns:1fr 32px;gap:10px;align-items:end;padding:12px 14px;border-radius:12px;background:${T.bgSoft};border:1.5px solid ${T.border};animation:fadeUp .2s ease;}
  .phone-row.invalid{border-color:#ffcccc;background:#fff8f8;}
  .err-box{background:#fff5f5;border:1px solid #ffcccc;border-radius:10px;padding:12px 16px;color:#c0392b;font-size:13px;display:flex;align-items:center;gap:8px;}
  .counter-badge{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;border-radius:999px;background:#25D366;color:#fff;font-size:10px;font-weight:700;font-family:'Sora',sans-serif;}
  .status-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;font-family:'Sora',sans-serif;}
  .status-chip.sent{background:#e6fff0;color:#1a7a40;}
  .status-chip.failed{background:#fff0f0;color:#c0392b;}
  .wa-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;background:#e8fff2;border:1px solid #b2f5c8;font-size:11px;font-weight:700;color:#1a7a40;font-family:'Sora',sans-serif;}
`;

// ── Types ────────────────────────────────────────────────────────
interface PhoneEntry {
  id:    string;
  phone: string;
}
interface SendResult {
  phone:   string;
  success: boolean;
  error?:  string;
}

const newEntry = (): PhoneEntry => ({
  id:    Math.random().toString(36).slice(2),
  phone: "",
});

const isValidPhone = (p: string) => /^\+?[1-9]\d{6,14}$/.test(p.replace(/[\s\-().]/g, ""));

const PREVIEW_COUNT = 5;

// ════════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════════
const WhatsAppMarketing: React.FC = () => {
  const user   = useSelector((s: RootState) => s.auth.user);
  const userId = user?.userId ?? "";

  const [message,  setMessage]  = useState("");
  const [entries,  setEntries]  = useState<PhoneEntry[]>([newEntry(), newEntry()]);
  const [showAll,  setShowAll]  = useState(false);
  const [sending,  setSending]  = useState(false);
  const [results,  setResults]  = useState<SendResult[] | null>(null);
  const [errMsg,   setErrMsg]   = useState("");
  const [showVal,  setShowVal]  = useState(false);

  const validEntries   = entries.filter(e => e.phone.trim() && isValidPhone(e.phone));
  const invalidEntries = entries.filter(e => e.phone.trim() && !isValidPhone(e.phone));
  const canSend        = validEntries.length > 0 && message.trim() && !sending;

  const displayed = showAll ? entries : entries.slice(0, PREVIEW_COUNT);

  const updateEntry = (id: string, phone: string) =>
    setEntries(p => p.map(e => e.id === id ? { ...e, phone } : e));

  const removeEntry = (id: string) =>
    setEntries(p => p.filter(e => e.id !== id));

  const addEntry = () => {
    setEntries(p => [...p, newEntry()]);
    if (!showAll && entries.length >= PREVIEW_COUNT) setShowAll(true);
  };

  const pasteList = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const phones = text
        .split(/[\n,;]+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => ({ id: Math.random().toString(36).slice(2), phone: s }));
      if (phones.length > 0) setEntries(p => [...p, ...phones]);
    } catch { /* clipboard denied */ }
  };

  const resetAll = () => {
    setMessage(""); setEntries([newEntry(), newEntry()]);
    setResults(null); setErrMsg(""); setShowAll(false); setShowVal(false);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true); setErrMsg(""); setResults(null);
    try {
      const res  = await fetch(WHATSAPP_BULK_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          phones:  validEntries.map(e => e.phone.trim()),
          message: message.trim(),
          sentBy:  userId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const apiResults: SendResult[] = data.results ?? validEntries.map(e => ({ phone: e.phone, success: true }));
      setResults(apiResults);
    } catch (e: any) {
      setErrMsg(e.message || "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  // ── Success view ──────────────────────────────────────────────
  if (results) {
    const sent   = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    return (
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif", padding:"40px 20px" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:36 }}>
            <div style={{ width:72, height:72, borderRadius:"50%", background:"#25D366", margin:"0 auto 20px", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <CheckCircle size={36} color="#fff"/>
            </div>
            <p style={{ fontFamily:"'Sora',sans-serif", fontSize:11, letterSpacing:"0.3em", marginBottom:8 }}>
              <span className="grad-text">WHATSAPP CAMPAIGN SENT</span>
            </p>
            <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:28, fontWeight:900, color:T.dark, marginBottom:8 }}>
              {sent.length} message{sent.length !== 1 ? "s" : ""} delivered 🎉
            </h2>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:28 }}>
            {[
              { label:"Sent",   count:sent.length,   color:"#1a7a40", bg:"#e6fff0", border:"#b2f5c8" },
              { label:"Failed", count:failed.length, color:"#c0392b", bg:"#fff5f5", border:"#ffcccc" },
            ].map(({ label, count, color, bg, border }) => (
              <div key={label} style={{ background:bg, border:`1.5px solid ${border}`, borderRadius:14, padding:"18px 20px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Sora',sans-serif", fontSize:32, fontWeight:900, color }}>{count}</div>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.12em", color, fontFamily:"'Sora',sans-serif" }}>{label.toUpperCase()}</div>
              </div>
            ))}
          </div>

          <div style={{ border:`1.5px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:28 }}>
            <div style={{ padding:"12px 16px", background:T.bgSoft, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:8 }}>
              <Phone size={13} color={T.muted}/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.1em", color:T.muted, fontFamily:"'Sora',sans-serif" }}>DELIVERY REPORT</span>
            </div>
            <div style={{ maxHeight:300, overflowY:"auto" }}>
              {results.map((r, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 16px", borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <span style={{ fontSize:13, color:T.dark, fontFamily:"'DM Sans',sans-serif" }}>{r.phone}</span>
                  <span className={`status-chip ${r.success ? "sent" : "failed"}`}>
                    {r.success ? "✓ Sent" : "✗ Failed"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"center" }}>
            <button className="btn-ghost" onClick={resetAll}>New Campaign</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif", paddingBottom:100 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:"rgba(255,255,255,.95)", backdropFilter:"blur(12px)", borderBottom:`1px solid ${T.border}` }}>
        <div style={{ height:3, background:"#25D366" }}/>
        <div style={{ maxWidth:660, margin:"0 auto", padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <h1 style={{ fontFamily:"'Sora',sans-serif", fontWeight:900, fontSize:17, color:T.dark, lineHeight:1, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:20 }}>💬</span> WhatsApp <span className="grad-text">Marketing</span>
            </h1>
            <p style={{ fontSize:11, color:T.muted, marginTop:3 }}>Send bulk messages to multiple phone numbers</p>
          </div>
          {validEntries.length > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"#e8fff2", border:"1px solid #b2f5c8", borderRadius:999 }}>
              <span className="counter-badge">{validEntries.length}</span>
              <span style={{ fontSize:11, color:"#1a7a40", fontFamily:"'Sora',sans-serif", fontWeight:600 }}>valid</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth:660, margin:"0 auto", padding:"28px 20px" }}>

        {/* Message */}
        <div style={{ marginBottom:28 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:"#25D366", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Send size={13} color="#fff"/>
            </div>
            <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:15, color:T.dark }}>Message</h2>
          </div>
          <label className="lbl">Message *</label>
          <textarea
            className="wa-textarea"
            placeholder="Write your WhatsApp message here…"
            value={message}
            onChange={e => setMessage(e.target.value)}
            style={{ minHeight:120 }}
          />
          <div style={{ textAlign:"right", fontSize:11, color: message.length > 1024 ? T.red : T.dim, marginTop:5, fontFamily:"'Sora',sans-serif" }}>
            {message.length} / 1024
          </div>
        </div>

        {/* Phone numbers */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:T.bgMuted, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Phone size={13} color={T.muted}/>
              </div>
              <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:15, color:T.dark }}>Phone Numbers</h2>
              {entries.length > 0 && (
                <span style={{ fontSize:11, color:T.muted }}>
                  {entries.length} row{entries.length !== 1 ? "s" : ""}{validEntries.length !== entries.length ? `, ${validEntries.length} valid` : ""}
                </span>
              )}
            </div>
            <button className="btn-ghost" style={{ fontSize:11, padding:"7px 14px" }} onClick={pasteList} title="Paste phone numbers from clipboard">
              Paste List
            </button>
          </div>

          <div style={{ padding:"0 14px", marginBottom:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:T.dim, letterSpacing:"0.1em", fontFamily:"'Sora',sans-serif" }}>PHONE NUMBER (include country code e.g. +447911123456)</span>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <AnimatePresence initial={false}>
              {displayed.map(entry => {
                const isDirty  = entry.phone.trim().length > 0;
                const isInvalid = isDirty && !isValidPhone(entry.phone);
                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity:0, y:8 }}
                    animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-6 }}
                    transition={{ duration:.16 }}
                  >
                    <div className={`phone-row${isInvalid ? " invalid" : ""}`}>
                      <div style={{ position:"relative" }}>
                        <div style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:T.muted, pointerEvents:"none" }}>
                          <Phone size={13}/>
                        </div>
                        <input
                          className="wa-input"
                          placeholder="+1 234 567 8900"
                          value={entry.phone}
                          onChange={e => updateEntry(entry.id, e.target.value)}
                          style={{ paddingLeft:34, paddingRight: isDirty ? 32 : 14 }}
                        />
                        {isDirty && (
                          <div style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                            {isInvalid
                              ? <AlertCircle size={13} color="#e84020"/>
                              : <CheckCircle size={13} color="#25D366"/>
                            }
                          </div>
                        )}
                      </div>
                      <button
                        className="btn-icon"
                        onClick={() => removeEntry(entry.id)}
                        disabled={entries.length <= 1}
                      >
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {entries.length > PREVIEW_COUNT && (
            <button
              style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:T.muted, fontFamily:"'Sora',sans-serif", fontWeight:700, marginTop:10, marginLeft:4, display:"flex", alignItems:"center", gap:5 }}
              onClick={() => setShowAll(v => !v)}
            >
              {showAll ? "▲ Show less" : `▼ Show ${entries.length - PREVIEW_COUNT} more`}
            </button>
          )}

          <button className="btn-ghost" onClick={addEntry} style={{ marginTop:12 }}>
            <Plus size={13}/> Add Number
          </button>

          {invalidEntries.length > 0 && showVal && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
              className="err-box" style={{ marginTop:14 }}>
              <AlertCircle size={14} color="#c0392b" style={{ flexShrink:0 }}/>
              {invalidEntries.length} number{invalidEntries.length > 1 ? "s are" : " is"} invalid and will be skipped.
            </motion.div>
          )}
        </div>

        {/* Summary */}
        {(message || validEntries.length > 0) && (
          <motion.div
            initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
            style={{ border:`1.5px solid ${T.border}`, borderRadius:12, padding:"14px 18px", background:T.bgSoft, marginBottom:20, display:"flex", flexWrap:"wrap", gap:16, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:3 }}>RECIPIENTS</div>
              <div style={{ fontSize:13, color:T.dark, fontWeight:600 }}>{validEntries.length} valid{invalidEntries.length > 0 ? `, ${invalidEntries.length} invalid` : ""}</div>
            </div>
            <div style={{ width:1, background:T.border, alignSelf:"stretch" }}/>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:3 }}>MESSAGE</div>
              <div style={{ fontSize:13, color:T.dark, fontWeight:600 }}>{message.length > 0 ? `${message.length} chars` : <span style={{ color:T.dim }}>—</span>}</div>
            </div>
            <div style={{ width:1, background:T.border, alignSelf:"stretch" }}/>
            <div className="wa-badge">💬 WhatsApp</div>
          </motion.div>
        )}

        {errMsg && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} className="err-box" style={{ marginBottom:20 }}>
            <AlertCircle size={14} color="#c0392b" style={{ flexShrink:0 }}/>
            {errMsg}
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0,
        background:"rgba(255,255,255,.97)", backdropFilter:"blur(12px)",
        borderTop:`1px solid ${T.border}`, padding:"14px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, zIndex:100,
      }}>
        <div style={{ fontSize:12, color:T.muted }}>
          {!canSend && !sending && (
            <span>
              {!message.trim() ? "Add a message" : validEntries.length === 0 ? "Add at least one valid phone number" : ""}
            </span>
          )}
          {canSend && (
            <span style={{ color:T.dark }}>
              Ready to send to <strong>{validEntries.length}</strong> number{validEntries.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <button
          className="btn-primary"
          disabled={!canSend}
          onClick={() => { setShowVal(true); handleSend(); }}
          style={{ minWidth:180 }}
        >
          {sending ? (
            <>
              <div style={{ width:14, height:14, borderRadius:"50%", border:"2px solid rgba(255,255,255,.4)", borderTop:"2px solid #fff", animation:"spin .7s linear infinite" }}/>
              Sending…
            </>
          ) : (
            <>
              💬 Send {validEntries.length > 0 ? `${validEntries.length} ` : ""}Message{validEntries.length !== 1 ? "s" : ""}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WhatsAppMarketing;
